import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ChevronRight, Grid2x2, LayoutGrid, List, SlidersHorizontal, X, ArrowUpDown } from 'lucide-react';
import api from '../services/api';
import ProductCard from '../components/product/ProductCard';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest Arrivals' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'sort_order', label: 'Featured Collections' },
];

const PER_PAGE_OPTIONS = [12, 24, 48];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<any[]>([]);
  const [category, setCategory] = useState<any>(null);
  const [pagination, setPagination] = useState({ total: 0, current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [gridCols, setGridCols] = useState(3);
  const [sortBy, setSortBy] = useState('newest');
  const [perPage, setPerPage] = useState(12);
  const [filterOpen, setFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });

  const fetchProducts = useCallback((page = 1) => {
    setLoading(true);
    const params: any = { page, per_page: perPage, sort_by: sortBy };
    if (slug) params.category = slug;
    if (priceRange.min) params.min_price = priceRange.min;
    if (priceRange.max) params.max_price = priceRange.max;
    if (searchParams.get('search')) params.search = searchParams.get('search');

    api.get('/products', { params })
      .then(r => {
        setProducts(r.data.data || r.data);
        if (r.data.meta) setPagination({ total: r.data.meta.total, current_page: r.data.meta.current_page, last_page: r.data.meta.last_page });
        else setPagination({ total: r.data.total || 0, current_page: r.data.current_page || 1, last_page: r.data.last_page || 1 });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug, sortBy, perPage, priceRange, searchParams]);

  useEffect(() => {
    if (slug) {
      api.get(`/categories/${slug}`).then(r => setCategory(r.data)).catch(() => {});
    } else {
      setCategory(null);
    }
    fetchProducts(1);
  }, [slug, sortBy, perPage, fetchProducts]);

  const gridConfig = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
  };

  const title = slug === 'special-prices' ? 'Special Offers' :
    searchParams.get('search') ? `Search Results: "${searchParams.get('search')}"` :
    category?.name || 'All Departments';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-gray-550 mb-8 overflow-x-auto whitespace-nowrap pb-2">
        <Link to="/" className="hover:text-primary transition-colors font-medium">Home</Link>
        <ChevronRight size={12} className="text-gray-400" />
        <span className="text-primary font-bold capitalize">{title}</span>
      </nav>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar Filter Menu - Desktop */}
        <aside className="hidden lg:block w-60 flex-shrink-0">
          <div className="sticky top-28 space-y-8 bg-white p-6 rounded-[24px] border border-gray-100 shadow-premium">
            <div className="space-y-4">
              <h3 className="text-xs font-extrabold text-primary uppercase tracking-widest border-b border-gray-50 pb-2">Categories</h3>
              <div className="space-y-1">
                {[
                  { name: 'New Arrivals', slug: 'new-in' },
                  { name: 'Adoptable Pets', slug: 'pets' },
                  { name: 'Pet Foods & Feed', slug: 'pet-foods' },
                  { name: 'Toys & Accessories', slug: 'pet-accessories' },
                ].map(cat => {
                  const isActive = slug === cat.slug;
                  return (
                    <Link
                      key={cat.slug}
                      to={cat.slug === 'new-in' ? `/${cat.slug}` : `/category/${cat.slug}`}
                      className={`flex items-center justify-between py-2.5 px-3 text-xs font-bold rounded-xl transition-all ${isActive ? 'bg-primary text-white shadow-sm' : 'text-gray-650 hover:bg-accent/10 hover:text-primary'}`}
                    >
                      <span>{cat.name}</span>
                      {isActive && <span className="w-1.5 h-1.5 rounded-full bg-accent" />}
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-gray-50">
              <h3 className="text-xs font-extrabold text-primary uppercase tracking-widest">Price Filter</h3>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={priceRange.min}
                  onChange={e => setPriceRange(p => ({ ...p, min: e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-xs focus:outline-none focus:border-accent bg-gray-50/50 rounded-xl transition-all"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={priceRange.max}
                  onChange={e => setPriceRange(p => ({ ...p, max: e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-xs focus:outline-none focus:border-accent bg-gray-50/50 rounded-xl transition-all"
                />
              </div>
              <button 
                onClick={() => fetchProducts(1)} 
                className="w-full bg-primary hover:bg-accent text-white py-2 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all shadow-sm active:scale-95"
              >
                Apply Range
              </button>
            </div>
          </div>
        </aside>

        {/* Catalog Main Flow */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Controls Toolbar Header */}
          <div className="bg-white px-5 py-4 rounded-2xl border border-gray-150/70 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
            
            {/* Mobile View Indicators */}
            <div className="flex items-center justify-between w-full lg:hidden">
              <button
                onClick={() => setFilterOpen(true)}
                className="flex items-center gap-2 border border-gray-200 bg-gray-55/10 px-4 py-2.5 text-xs font-bold text-gray-700 hover:border-accent transition-all rounded-xl"
              >
                <SlidersHorizontal size={14} /> Filter Options
              </button>
              
              <div className="flex items-center gap-2">
                <div className="flex gap-1.5 border border-gray-100 p-0.5 rounded-lg bg-gray-50/50">
                  {[2, 3].map(cols => (
                    <button 
                      key={cols} 
                      onClick={() => setGridCols(cols as 2|3|4)} 
                      className={`p-1.5 rounded-md transition-colors ${gridCols === cols ? 'bg-white text-primary shadow-sm' : 'text-gray-450 hover:text-primary'}`}
                      title={`${cols} Column Grid`}
                    >
                      {cols === 2 ? <List size={14} /> : <Grid2x2 size={14} />}
                    </button>
                  ))}
                </div>
                <select 
                  aria-label="Sort options mobile" 
                  value={sortBy} 
                  onChange={e => setSortBy(e.target.value)} 
                  className="border border-gray-200 px-3 py-2 text-xs font-semibold focus:outline-none rounded-xl text-gray-700 bg-white shadow-sm"
                >
                  {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            {/* Desktop View Indicators */}
            <div className="hidden lg:flex items-center gap-4">
              <span className="text-xs font-bold text-gray-550 uppercase tracking-wider">Layout View:</span>
              <div className="flex gap-1 border border-gray-100 p-0.5 rounded-lg bg-[#FCFAF7]">
                {[2, 3, 4].map(cols => (
                  <button 
                    key={cols} 
                    onClick={() => setGridCols(cols as 2|3|4)} 
                    className={`p-1.5 rounded-md transition-all ${gridCols === cols ? 'bg-white text-primary shadow-sm' : 'text-gray-450 hover:text-primary'}`}
                    title={`${cols} Columns`}
                  >
                    {cols === 2 ? <List size={14} /> : cols === 3 ? <Grid2x2 size={14} /> : <LayoutGrid size={14} />}
                  </button>
                ))}
              </div>
            </div>

            <div className="hidden lg:flex items-center gap-6">
              <div className="flex items-center gap-2 text-xs font-bold text-gray-550 uppercase tracking-wider">
                <span>Display:</span>
                <select 
                  aria-label="Items per page desktop" 
                  value={perPage} 
                  onChange={e => setPerPage(Number(e.target.value))} 
                  className="border border-gray-200 px-2.5 py-1.5 text-xs font-bold focus:outline-none rounded-xl text-gray-700 bg-white"
                >
                  {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n} Items</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 text-xs font-bold text-gray-550 uppercase tracking-wider">
                <ArrowUpDown size={12} className="text-gray-450" />
                <span>Sort:</span>
                <select 
                  aria-label="Sort options desktop" 
                  value={sortBy} 
                  onChange={e => setSortBy(e.target.value)} 
                  className="border border-gray-200 px-2.5 py-1.5 text-xs font-bold focus:outline-none rounded-xl text-gray-700 bg-white"
                >
                  {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

          </div>

          {/* Mobile Filter Drawer */}
          {filterOpen && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div className="absolute inset-0 bg-primary/40 backdrop-blur-sm transition-opacity" onClick={() => setFilterOpen(false)} />
              <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[32px] shadow-premium max-h-[85vh] flex flex-col overflow-hidden animate-slide-up">
                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
                  <div>
                    <h2 className="text-base font-serif font-extrabold text-primary">Filter Catalog</h2>
                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Refine product feed</p>
                  </div>
                  <button aria-label="Close filters" onClick={() => setFilterOpen(false)} className="p-2 text-gray-400 hover:text-primary hover:bg-gray-50 rounded-xl transition-all">
                    <X size={18} />
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                  {/* Categories List */}
                  <div className="space-y-3">
                    <h3 className="text-xs font-extrabold text-primary uppercase tracking-wider">Departments</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { name: 'New Arrivals', slug: 'new-in' },
                        { name: 'Adoptable Pets', slug: 'pets' },
                        { name: 'Pet Foods & Feed', slug: 'pet-foods' },
                        { name: 'Toys & Accessories', slug: 'pet-accessories' },
                      ].map(cat => (
                        <Link
                          key={cat.slug}
                          to={cat.slug === 'new-in' ? `/${cat.slug}` : `/category/${cat.slug}`}
                          onClick={() => setFilterOpen(false)}
                          className={`px-3 py-3 text-xs font-bold border rounded-xl text-center transition-all ${slug === cat.slug ? 'border-primary bg-primary text-white shadow-sm' : 'border-gray-200 text-gray-700 bg-white hover:border-accent'}`}
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                  
                  {/* Price sliders */}
                  <div className="space-y-3">
                    <h3 className="text-xs font-extrabold text-primary uppercase tracking-wider">Price Range</h3>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        placeholder="Min Price"
                        value={priceRange.min}
                        onChange={e => setPriceRange(p => ({ ...p, min: e.target.value }))}
                        className="flex-1 border border-gray-200 px-4 py-3 text-xs focus:outline-none focus:border-accent bg-gray-55/10 rounded-xl"
                      />
                      <span className="text-gray-400 text-sm flex-shrink-0">—</span>
                      <input
                        type="number"
                        placeholder="Max Price"
                        value={priceRange.max}
                        onChange={e => setPriceRange(p => ({ ...p, max: e.target.value }))}
                        className="flex-1 border border-gray-200 px-4 py-3 text-xs focus:outline-none focus:border-accent bg-gray-55/10 rounded-xl"
                      />
                    </div>
                  </div>
                  
                  {/* Grid layout sizes */}
                  <div className="space-y-3">
                    <h3 className="text-xs font-extrabold text-primary uppercase tracking-wider">Items Per Page</h3>
                    <div className="flex gap-2.5">
                      {PER_PAGE_OPTIONS.map(n => (
                        <button
                          key={n}
                          onClick={() => setPerPage(n)}
                          className={`px-5 py-2.5 text-xs font-bold border rounded-xl transition-all ${perPage === n ? 'border-primary bg-primary text-white' : 'border-gray-200 text-gray-700 bg-white'}`}
                        >
                          {n} Items
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Filter Drawer CTAs */}
                <div className="px-6 py-5 border-t border-gray-100 flex gap-4 bg-[#FCFAF7] flex-shrink-0">
                  <button
                    onClick={() => { setPriceRange({ min: '', max: '' }); }}
                    className="flex-1 border-2 border-gray-200 text-gray-700 py-3 text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-gray-50 active:scale-95 transition-all"
                  >
                    Clear Filter
                  </button>
                  <button
                    onClick={() => { fetchProducts(1); setFilterOpen(false); }}
                    className="flex-1 bg-primary hover:bg-accent text-white py-3 text-[10px] font-bold uppercase tracking-widest rounded-xl active:scale-95 shadow-md transition-all"
                  >
                    Apply Filter
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Catalog grid list */}
          {loading ? (
            <div className={`grid ${gridConfig[gridCols as keyof typeof gridConfig]} gap-6`}>
              {Array(8).fill(null).map((_, i) => (
                <div key={i} className="animate-pulse bg-white border border-gray-150/40 rounded-3xl p-4 space-y-4">
                  <div className="bg-gray-100 aspect-[4/5] w-full rounded-2xl" />
                  <div className="h-4 bg-gray-100 mt-3 w-3/4 rounded-full" />
                  <div className="h-4 bg-gray-100 mt-2 w-1/2 rounded-full" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24 bg-white border border-gray-100 rounded-[32px] shadow-sm space-y-4">
              <div className="w-14 h-14 bg-gray-50 rounded-full flex items-center justify-center mx-auto text-gray-350">
                <SlidersHorizontal size={22} />
              </div>
              <p className="text-primary font-bold text-sm">No items found matching your filters</p>
              <p className="text-gray-400 text-xs mt-1 max-w-xs mx-auto">Try updating your pricing ranges or browse other departments.</p>
              <button 
                onClick={() => { setPriceRange({ min: '', max: '' }); fetchProducts(1); }}
                className="mt-6 bg-primary hover:bg-accent text-white px-8 py-3.5 text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-md active:scale-95"
              >
                Reset Price Filters
              </button>
            </div>
          ) : (
            <>
              <div className={`grid ${gridConfig[gridCols as keyof typeof gridConfig]} gap-6`}>
                {products.map(product => <ProductCard key={product.id} product={product} />)}
              </div>

              {/* Refined Pagination Controls */}
              {pagination.last_page > 1 && (
                <div className="flex justify-center gap-2 mt-16">
                  {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => fetchProducts(page)}
                      className={`w-10 h-10 text-xs font-bold border transition-all rounded-xl active:scale-90 ${page === pagination.current_page ? 'border-primary bg-primary text-white shadow-md' : 'border-gray-200 text-gray-700 bg-white hover:border-accent'}`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
